import DOMPurify from "dompurify";

/**
 * Compact Markdown → sanitised-HTML converter shared by the marketing-site
 * LegalDrawer and the app-wide LegalDocDialog. Covers headings, bold/italic,
 * links, ordered/unordered lists, code fences and paragraphs. The output is run
 * through DOMPurify before it is ever inserted into the DOM.
 *
 * Extracted verbatim from pages/site/components/LegalDrawer.tsx so the two
 * viewers cannot drift in how they render the same /legal/*.md documents.
 */
const mdToHtml = (md: string): string => {
    if (!md) return "";
    let s = md.replace(/\r\n/g, "\n");
    const parts = s.split(/```/g);
    for (let i = 1; i < parts.length; i += 2) {
        parts[i] = `<pre><code>${parts[i].replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code></pre>`;
    }
    s = parts.join("");
    s = s.replace(/^######\s+(.*)$/gm, "<h6>$1</h6>")
        .replace(/^#####\s+(.*)$/gm, "<h5>$1</h5>")
        .replace(/^####\s+(.*)$/gm, "<h4>$1</h4>")
        .replace(/^###\s+(.*)$/gm, "<h3>$1</h3>")
        .replace(/^##\s+(.*)$/gm, "<h2>$1</h2>")
        .replace(/^#\s+(.*)$/gm, "<h1>$1</h1>");
    s = s.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(?<!\*)\*(?!\*)([^\n*]+)\*(?!\*)/g, "<em>$1</em>");
    s = s.replace(/_([^\n_]+)_/g, "<em>$1</em>");
    s = s.replace(/\[(.+?)\]\((https?:[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    s = s.replace(/^(\d+)\.\s+(.*)$/gm, "<li>$2</li>");
    s = s.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ol>${m}</ol>`);
    s = s.replace(/^[-*]\s+(.*)$/gm, "<li>$1</li>");
    s = s.replace(/(<li>.*<\/li>\n?)+/g, (m) => (m.startsWith("<ol>") ? m : `<ul>${m}</ul>`));
    s = s.replace(/^(?!<h\d|<ul>|<ol>|<li>|<pre>|<blockquote>|<p>|\s*$)(.+)$/gm, "<p>$1</p>");
    return s;
};

/** Convert raw markdown to a DOMPurify-sanitised HTML string. */
export const renderLegalMarkdown = (markdown: string): string => DOMPurify.sanitize(mdToHtml(markdown));

/**
 * Fetch a /legal/*.md document for the given locale, falling back to the
 * English file (the convention LegalDrawer already relies on for locales that
 * reuse the EN copy). Returns the raw markdown text.
 *
 * @param buildUrl maps a locale → document URL (e.g. `/legal/cookie-policy.${l}.md`)
 */
export const fetchLegalDoc = async (
    buildUrl: (locale: string) => string,
    locale: string,
    fallbackLocale = "en"
): Promise<string> => {
    try {
        const res = await fetch(buildUrl(locale), { cache: "no-store" });
        if (!res.ok) throw new Error("not ok");
        return await res.text();
    } catch {
        try {
            const res2 = await fetch(buildUrl(fallbackLocale), { cache: "no-store" });
            return await res2.text();
        } catch {
            return "";
        }
    }
};
